import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, CalendarRange, Trash2, Loader2, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { CATEGORY, PRIORITY, initials, fmtDate, AgingBadge, Dot } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function startOfWeek() {
  const d = new Date();
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}
function endOfWeek() {
  const d = new Date(startOfWeek());
  d.setDate(d.getDate() + 6);
  return d.toISOString().slice(0, 10);
}

function CreateSprintDialog({ onClose, onCreated }) {
  const [name, setName] = useState(`Sprint — Week of ${fmtDate(startOfWeek())}`);
  const [start, setStart] = useState(startOfWeek());
  const [end, setEnd] = useState(endOfWeek());
  const [allTasks, setAllTasks] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/tasks").then((r) => setAllTasks(r.data)); }, []);

  const submit = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!name.trim()) { toast.error("Name required"); return; }
    setLoading(true);
    try {
      await api.post("/sprints", { name, start_date: start, end_date: end, task_ids: ids });
      toast.success("Sprint created");
      onCreated?.(); onClose();
    } catch { toast.error("Could not create sprint"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="create-sprint-dialog">
        <DialogHeader><DialogTitle className="font-display">Plan a weekly sprint</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label>Sprint name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="sprint-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Start</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="sprint-start-input" /></div>
            <div className="space-y-2"><Label>End</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="sprint-end-input" /></div>
          </div>
          <div className="space-y-2">
            <Label>Pick tasks from any board ({Object.values(selected).filter(Boolean).length} selected)</Label>
            <div className="max-h-64 overflow-y-auto scroll-thin border border-slate-200 rounded-lg divide-y divide-slate-100">
              {allTasks.map((t) => (
                <label key={t.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`sprint-pick-${t.id}`}>
                  <Checkbox checked={!!selected[t.id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [t.id]: v }))} />
                  <Dot className={PRIORITY[t.priority]?.dot} />
                  <span className="flex-1 text-sm text-slate-700 truncate">{t.title}</span>
                  <Badge variant="outline" className="h-4 px-1.5 text-[10px] border-slate-200 text-slate-500">{t.board_name}</Badge>
                </label>
              ))}
              {allTasks.length === 0 && <p className="text-sm text-slate-400 p-4">No tasks available.</p>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="sprint-create-submit">
            {loading ? "Creating…" : "Create sprint"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SprintBoard({ sprint, isManager, onRemoveTask }) {
  const cols = ["backlog", "in_progress", "in_review", "completed"];
  const byCat = (c) => sprint.tasks.filter((t) => t.category === c);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      {cols.map((c) => (
        <div key={c} className="bg-slate-100/70 border border-slate-200/80 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-3 px-1">
            <Badge variant="outline" className={`h-5 px-1.5 text-[10px] ${CATEGORY[c].chip}`}>{CATEGORY[c].label}</Badge>
            <span className="text-xs text-slate-400">{byCat(c).length}</span>
          </div>
          <div className="space-y-2.5">
            {byCat(c).map((t) => (
              <div key={t.id} className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm space-y-2" data-testid={`sprint-task-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-slate-900 leading-snug">{t.title}</p>
                  <Badge variant="outline" className={`shrink-0 h-5 px-1.5 text-[10px] ${PRIORITY[t.priority].badge}`}>{PRIORITY[t.priority].label}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Badge variant="outline" className="h-4 px-1.5 text-[10px] border-slate-200 text-slate-500">{t.board_name}</Badge>
                    <AgingBadge age={t.age_days} inStage={t.days_in_stage} />
                  </div>
                  {t.assignees?.length > 0 && (
                    <div className="flex -space-x-1.5" title={t.assignee_name} data-testid={`sprint-task-assignees-${t.id}`}>
                      {t.assignees.slice(0, 3).map((a) => <Avatar key={a.id} className="w-6 h-6 ring-2 ring-white"><AvatarImage src={a.avatar} /><AvatarFallback className="bg-indigo-100 text-indigo-700 text-[9px]">{initials(a.name)}</AvatarFallback></Avatar>)}
                      {t.assignees.length > 3 && <span className="w-6 h-6 rounded-full ring-2 ring-white bg-slate-100 text-[9px] font-semibold flex items-center justify-center">+{t.assignees.length - 3}</span>}
                    </div>
                  )}
                </div>
                {isManager && <button onClick={() => onRemoveTask(t.id)} className="text-[11px] text-slate-400 hover:text-red-500">Remove</button>}
              </div>
            ))}
            {byCat(c).length === 0 && <p className="text-xs text-slate-400 text-center py-3">—</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SprintsPage() {
  const { user } = useAuth();
  const isManager = user?.role !== "engineer";
  const [sprints, setSprints] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);

  const loadList = async () => {
    const { data } = await api.get("/sprints");
    setSprints(data);
    if (!activeId && data.length) setActiveId(data[0].id);
  };
  useEffect(() => { loadList(); }, []);
  useEffect(() => { if (activeId) api.get(`/sprints/${activeId}`).then((r) => setActive(r.data)); }, [activeId, sprints]);

  const removeTask = async (taskId) => {
    const ids = active.task_ids.filter((i) => i !== taskId);
    await api.patch(`/sprints/${active.id}`, { task_ids: ids });
    const { data } = await api.get(`/sprints/${active.id}`);
    setActive(data); loadList();
  };
  const deleteSprint = async () => {
    if (!window.confirm("Delete this sprint?")) return;
    await api.delete(`/sprints/${active.id}`);
    toast.success("Sprint deleted");
    setActiveId(null); setActive(null); loadList();
  };

  const pct = active && active.task_count ? Math.round((active.completed_count / active.task_count) * 100) : 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]" data-testid="sprints-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Weekly Sprints</h1>
          <p className="text-sm text-slate-500 mt-1">Focus the team on what matters this week — pull tasks from any board.</p>
        </div>
        {isManager && (
          <Button onClick={() => setCreating(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-sprint-button">
            <Plus className="w-4 h-4 mr-1.5" /> New sprint
          </Button>
        )}
      </div>

      {sprints.length > 0 && (
        <div className="flex gap-2 mb-6 overflow-x-auto scroll-thin pb-1">
          {sprints.map((s) => (
            <button key={s.id} onClick={() => setActiveId(s.id)} data-testid={`sprint-tab-${s.id}`}
              className={`shrink-0 text-left px-4 py-3 rounded-xl border transition-all ${activeId === s.id ? "bg-white border-indigo-300 shadow-sm" : "bg-white/60 border-slate-200 hover:border-slate-300"}`}>
              <p className="text-sm font-semibold text-slate-800">{s.name}</p>
              <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><CalendarRange className="w-3 h-3" />{fmtDate(s.start_date)} – {fmtDate(s.end_date)}</p>
              <p className="text-xs text-emerald-600 font-medium mt-0.5">{s.completed_count}/{s.task_count} done</p>
            </button>
          ))}
        </div>
      )}

      {!active ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl py-20 text-center text-slate-400">
          <CalendarRange className="w-8 h-8 mx-auto mb-2 opacity-40" />
          No sprint selected. {isManager ? "Create one to get started." : "Ask a manager to plan this week's sprint."}
        </div>
      ) : (
        <div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-display text-xl font-bold text-slate-900">{active.name}</h2>
              <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-1"><CalendarRange className="w-4 h-4" />{fmtDate(active.start_date)} – {fmtDate(active.end_date)}</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="w-48">
                <div className="flex items-center justify-between text-xs mb-1"><span className="text-slate-500">Progress</span><span className="font-semibold text-slate-700">{pct}%</span></div>
                <Progress value={pct} className="h-2.5" />
                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{active.completed_count} of {active.task_count} tasks</p>
              </div>
              {isManager && <Button variant="ghost" className="text-red-600 hover:bg-red-50" onClick={deleteSprint} data-testid="sprint-delete-button"><Trash2 className="w-4 h-4" /></Button>}
            </div>
          </div>
          {active.tasks?.length ? <SprintBoard sprint={active} isManager={isManager} onRemoveTask={removeTask} />
            : <p className="text-sm text-slate-400 text-center py-10">No tasks in this sprint yet.</p>}
        </div>
      )}

      {creating && <CreateSprintDialog onClose={() => setCreating(false)} onCreated={loadList} />}
    </div>
  );
}
